/**
 * ProductListPage — seller product catalog list (EPIC-006 Phase 7, Task 7.1;
 * story 006-011 + 006-005/009 front end).
 *
 * Surfaces:
 *   - a sticky `VacationToggle` (hides all products from the storefront);
 *   - filters (status / stock_state / category) via `--g-*` pills + a category
 *     <select> driven by the public category tree;
 *   - a multi-select table (photo thumb, name, category, `price_kgs` formatted
 *     RU `1 234,56`, stock pill, status, actions) that collapses to cards
 *     below 640px;
 *   - a bulk toolbar (Set stock-state radio → `:bulk-stock-state`, Soft delete).
 *
 * Auth model (AC#2): the route guard is OPTIMISTIC (auth-only). This page is
 * where the seller-ownership 403 is handled — a `403 forbidden.not_a_seller`
 * (or `forbidden.seller_suspended`) from the list query redirects to `/account`
 * with a toast.
 *
 * Money: `price_kgs` is a Decimal-as-string; parsed once with `parseDecimal`
 * and formatted at the display boundary via `fmtNum` (RU grouping). No tiyin.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { GPill, type GPillTone } from "@garden/ui";

import { ThemeToggle } from "@/components/ThemeToggle";
import {
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
  STOCK_STATES,
  PRODUCT_STATUSES,
  type ProductListFilters,
  type SellerProduct,
} from "@/features/seller-dashboard/api";
import {
  useBulkSetStockState,
  useDeleteProduct,
  useSellerProducts,
  LIST_PAGE_SIZE,
} from "@/features/seller-dashboard/hooks/useSellerProducts";
import { VacationToggle } from "@/features/seller-dashboard/VacationToggle";
import {
  useCategoryTree,
  categoryDisplayName,
  findNodeInTree,
  type Category,
} from "@/features/marketplace/useCategoryTree";
import { ApiError } from "@/lib/api-client";
import { fmtNum, parseDecimal } from "@/lib/format";

/** Tone for a stock-state pill — semantic `--g-*` tones only. */
function stockTone(state: string): GPillTone {
  switch (state) {
    case "in_stock":
      return "success";
    case "low":
      return "warn";
    case "out_of_stock":
      return "error";
    default:
      return "neutral";
  }
}

/** Tone for a product-status pill (PIM Wave P3) — mirrors the admin/
 * seller-applications `statusTone()` precedent. */
function productStatusTone(status: string): GPillTone {
  switch (status) {
    case "rejected":
      return "error";
    case "pending_approval":
      return "warn";
    case "active":
      return "success";
    default:
      return "neutral"; // draft / hidden_by_seller / soft_deleted
  }
}

/** Flatten the category tree to leaf+branch options for the filter <select>. */
function flattenCategories(
  tree: readonly Category[],
  language: string,
  depth = 0,
): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  for (const node of tree) {
    const indent = "  ".repeat(depth);
    out.push({ id: node.id, label: `${indent}${categoryDisplayName(node, language)}` });
    if (node.children.length > 0) {
      out.push(...flattenCategories(node.children, language, depth + 1));
    }
  }
  return out;
}

/** Format an ISO-8601 `updated_at` as a short locale date (e.g. `13.06.2026`). */
function formatUpdated(iso: string, language: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // RU/KY both read a dd.mm.yyyy date well; `ru` is the safe fallback locale.
  const locale = language === "en" ? "en-GB" : "ru-RU";
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Format a nullable Decimal-string price as RU `1 234,56` (no tiyin). */
function formatPrice(value: string | null, noPrice: string): string {
  if (value === null) return noPrice;
  const n = parseDecimal(value);
  const fixed = n.toFixed(2); // always 2 decimals
  const [intPart, decPart] = fixed.split(".");
  return `${fmtNum(Number(intPart))},${decPart}`;
}

/**
 * The same, but for a product that sells in a BAND (v0052).
 *
 * `price_kgs` is only the LOW end once `price_max_kgs` is set, so a seller
 * looking at their own list saw 650 for a product listed at 650-1 000 — the
 * figure they are least able to afford to misread, on their own stock.
 */
function formatPriceBand(
  value: string | null,
  max: string | null | undefined,
  noPrice: string,
): string {
  const low = formatPrice(value, noPrice);
  if (value === null || max == null) return low;
  // A degenerate band (equal ends, or a max below the min) is an exact price
  // written twice; "650,00 – 650,00" would be noise, not information.
  if (parseDecimal(max) <= parseDecimal(value)) return low;
  return `${low} – ${formatPrice(max, noPrice)}`;
}

// SCRUM-169: client-side sort over the currently-loaded page(s) — the
// backend list endpoint is keyset-paginated on a fixed `created_at desc, id
// desc` order (repository.list_for_seller), so an arbitrary-column sort
// would need a per-column cursor scheme; sorting what's already loaded is
// the scoped fix for "the header click does nothing".
type SortColumn = "name" | "category" | "price" | "stock" | "status" | "updated";
type SortDirection = "asc" | "desc";
interface SortState {
  column: SortColumn;
  direction: SortDirection;
}

function nextSort(current: SortState | null, column: SortColumn): SortState {
  if (current && current.column === column) {
    return { column, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return { column, direction: "asc" };
}

function ariaSortFor(sort: SortState | null, column: SortColumn): "ascending" | "descending" | "none" {
  if (sort === null || sort.column !== column) return "none";
  return sort.direction === "asc" ? "ascending" : "descending";
}

/** A sortable `<th>` — click toggles asc/desc on `column`; mirrors the
 * AdminDataTable sort-icon idiom (ChevronsUpDown when idle, ArrowUp/Down
 * when active) for visual consistency with the rest of the app. */
function SortableTh({
  column,
  label,
  sort,
  onSort,
  ascLabel,
  descLabel,
}: {
  column: SortColumn;
  label: string;
  sort: SortState | null;
  onSort: (column: SortColumn) => void;
  ascLabel: string;
  descLabel: string;
}): ReactElement {
  const sorted = sort?.column === column;
  const SortIcon = !sorted ? ChevronsUpDown : sort?.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th scope="col" className="px-3 py-2" aria-sort={ariaSortFor(sort, column)}>
      <button
        type="button"
        onClick={() => onSort(column)}
        aria-label={
          sorted ? `${label}, ${sort?.direction === "desc" ? descLabel : ascLabel}` : label
        }
        data-testid={`sort-${column}`}
        className="inline-flex items-center gap-1 rounded-[var(--g-radius-sm)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {label}
        <SortIcon size={14} strokeWidth={1.75} aria-hidden="true" />
      </button>
    </th>
  );
}

/** A contiguous window of page numbers (<=`size`) centred on `current`,
 * clamped to `[1, total]` — keeps the nav a fixed, small width however many
 * pages have loaded. Mirrors the admin list's helper (SCRUM-165). */
function pageWindow(current: number, total: number, size = 5): number[] {
  if (total <= size) return Array.from({ length: total }, (_, i) => i + 1);
  let start = Math.max(1, current - Math.floor(size / 2));
  const end = Math.min(total, start + size - 1);
  start = Math.max(1, end - size + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function ProductListPage(): ReactElement {
  registerSellerDashboardI18n();
  const { t, i18n } = useTranslation(SELLER_DASHBOARD_NS);

  const [filters, setFilters] = useState<ProductListFilters>({
    status: null,
    stock_state: null,
    category_id: null,
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkStock, setBulkStock] = useState<string>("in_stock");
  const [sort, setSort] = useState<SortState | null>(null);
  // SCRUM-239 — client-side name filter. `GET /sellers/me/products` has no
  // free-text `q`, the same situation the admin products list documents.
  const [search, setSearch] = useState("");
  // SCRUM-193 — numbered pages, chunked client-side over accumulated cursor
  // pages (the endpoint is keyset-paginated, Addendum §6.4: no total, no
  // page=N). Mirrors SCRUM-165 on the admin list.
  const [currentPage, setCurrentPage] = useState(1);

  const productsQuery = useSellerProducts(filters);
  const { tree } = useCategoryTree();
  const bulkMutation = useBulkSetStockState();
  const deleteMutation = useDeleteProduct();

  const products: SellerProduct[] = useMemo(
    () => productsQuery.data?.pages.flatMap((p) => p.products) ?? [],
    [productsQuery.data],
  );

  const categoryOptions = useMemo(
    () => flattenCategories(tree, i18n.language),
    [tree, i18n.language],
  );

  const categoryLabel = (id: string): string => {
    const node = findNodeInTree(tree, id);
    return node ? categoryDisplayName(node, i18n.language) : "—";
  };

  const sortedProducts = useMemo(() => {
    if (sort === null) return products;
    const { column, direction } = sort;
    const dir = direction === "asc" ? 1 : -1;
    const key = (p: SellerProduct): string | number => {
      switch (column) {
        case "name":
          return p.name.toLocaleLowerCase();
        case "category":
          return categoryLabel(p.category_id).toLocaleLowerCase();
        case "price":
          return p.price_kgs === null ? -Infinity : parseDecimal(p.price_kgs);
        case "stock":
          return STOCK_STATES.indexOf(p.stock_state as (typeof STOCK_STATES)[number]);
        case "status":
          return PRODUCT_STATUSES.indexOf(p.status as (typeof PRODUCT_STATUSES)[number]);
        case "updated":
          return new Date(p.updated_at).getTime();
      }
    };
    return [...products].sort((a, b) => {
      const ka = key(a);
      const kb = key(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- categoryLabel closes over tree/i18n.language, both already covered by categoryOptions' identical deps; re-declaring it as a dep would just re-run this memo on every render since it's a new function each time.
  }, [products, sort, tree, i18n.language]);

  // SCRUM-239 — substring match on the product name, case- and
  // accent-insensitively via `localeCompare`-friendly lowercasing.
  const filteredProducts = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    if (needle === "") return sortedProducts;
    return sortedProducts.filter((p) =>
      p.name.toLocaleLowerCase().includes(needle),
    );
  }, [sortedProducts, search]);

  // A client-side filter is only honest if the rows are all HERE. A seller's
  // own catalogue is bounded and seller-scoped, so while a search is active we
  // pull the remaining cursor pages instead of quietly matching the first 25 —
  // otherwise the page reports "nothing found" for a product the seller is
  // looking at in their own shop (SCRUM-239).
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = productsQuery;
  useEffect(() => {
    if (search.trim() === "") return;
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [search, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const totalKnownPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / LIST_PAGE_SIZE),
  );
  // Show ONE page beyond what is loaded whenever more exist. Without it the
  // nav renders a lone "1" until something is fetched, and the ticket's actual
  // ask — "jump to a specific page" — is unmet on first paint. Clicking that
  // number is what triggers the fetch (see `goToPage`).
  const totalPages = totalKnownPages + (productsQuery.hasNextPage ? 1 : 0);
  const pageProducts = useMemo(
    () =>
      filteredProducts.slice(
        (currentPage - 1) * LIST_PAGE_SIZE,
        currentPage * LIST_PAGE_SIZE,
      ),
    [filteredProducts, currentPage],
  );

  // A filter/search/sort change swaps out the whole result set — page 2 of the
  // old set is nowhere in the new one, and staying there shows an empty list
  // for a search that DID match.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filters.status, filters.stock_state, filters.category_id, sort]);

  const goToPage = useCallback(
    (page: number): void => {
      const clamped = Math.max(1, page);
      setCurrentPage(clamped);
      // The nav only exposes loaded pages plus one Next step, so stepping past
      // the end just means fetching the next cursor page — the same call the
      // old "Show more" button made.
      if (clamped > totalKnownPages && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    [totalKnownPages, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // ── 403 redirect (AC#2): not-a-seller / suspended → /account + toast. ──────
  const queryError = productsQuery.error;
  if (queryError instanceof ApiError && queryError.status === 403) {
    toast.error(t("forbidden"));
    return <Navigate to="/account" replace />;
  }

  const toggleRow = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = (): void => setSelected(new Set());

  const handleBulkStock = (): void => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    bulkMutation.mutate(
      { productIds: ids, stockState: bulkStock },
      {
        onSuccess: (res) => {
          toast.success(t("bulk.applied", { count: res.affected.length }));
          clearSelection();
        },
        onError: () => toast.error(t("bulk.failed")),
      },
    );
  };

  const handleBulkDelete = (): void => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    void Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id)),
    ).then((results) => {
      const ok = results.filter((r) => r.status === "fulfilled").length;
      if (ok > 0) toast.success(t("bulk.deletedCount", { count: ok }));
      if (ok < ids.length) toast.error(t("bulk.failed"));
      clearSelection();
    });
  };

  const setFilter = (key: keyof ProductListFilters, value: string | null): void => {
    setFilters((prev) => ({ ...prev, [key]: prev[key] === value ? null : value }));
    clearSelection();
  };

  const allSelected = products.length > 0 && selected.size === products.length;
  const toggleAll = (): void => {
    if (allSelected) clearSelection();
    else setSelected(new Set(products.map((p) => p.id)));
  };

  const toggleSort = (column: SortColumn): void => setSort((cur) => nextSort(cur, column));

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6" data-testid="product-list-page">
      <VacationToggle />

      {/* Header */}
      <header className="mb-4 mt-2 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">{t("list.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("list.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link
            to="/sellers/me/products/new"
            className={[
              "inline-flex min-h-[44px] items-center rounded-[var(--g-radius)] bg-primary",
              "px-4 text-sm font-semibold text-[var(--g-on-primary)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            ].join(" ")}
            data-testid="new-product-link"
          >
            {t("list.newProduct")}
          </Link>
        </div>
      </header>

      {/* Filters */}
      <section
        className="mb-4 flex flex-wrap items-center gap-2"
        aria-label={t("filters.title")}
        data-testid="product-filters"
      >
        {/* SCRUM-239 — the seller could previously only narrow by stock chips
            and two dropdowns, so finding one item in a 100+ catalogue meant
            scrolling. */}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("list.searchPlaceholder")}
          aria-label={t("list.searchLabel")}
          data-testid="seller-products-search"
          className="min-h-[44px] min-w-[12rem] flex-1 rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {STOCK_STATES.map((s) => (
          <GPill
            key={s}
            tone="neutral"
            active={filters.stock_state === s}
            onClick={() => setFilter("stock_state", s)}
          >
            {t(`stock.${s}`)}
          </GPill>
        ))}
        <select
          aria-label={t("filters.status")}
          data-testid="filter-status"
          value={filters.status ?? ""}
          onChange={(e) => setFilter("status", e.target.value || null)}
          className="min-h-[44px] rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground"
        >
          <option value="">{t("filters.all")}</option>
          {PRODUCT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`filters.statusValues.${s}`)}
            </option>
          ))}
        </select>
        <select
          aria-label={t("filters.category")}
          data-testid="filter-category"
          value={filters.category_id ?? ""}
          onChange={(e) => setFilter("category_id", e.target.value || null)}
          className="min-h-[44px] max-w-[220px] rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground"
        >
          <option value="">{t("filters.all")}</option>
          {categoryOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </section>

      {/* Bulk toolbar */}
      {selected.size > 0 ? (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 rounded-[var(--g-radius)] border border-border bg-secondary px-4 py-3"
          data-testid="bulk-toolbar"
          role="region"
          aria-label={t("bulk.setStock")}
        >
          <span className="text-sm font-semibold text-secondary-foreground">
            {t("bulk.selected", { count: selected.size })}
          </span>
          <fieldset className="flex flex-wrap items-center gap-2" data-testid="bulk-stock-radios">
            <legend className="sr-only">{t("bulk.setStock")}</legend>
            {STOCK_STATES.map((s) => (
              <label key={s} className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-foreground">
                <input
                  type="radio"
                  name="bulk-stock"
                  value={s}
                  checked={bulkStock === s}
                  onChange={() => setBulkStock(s)}
                  className="accent-[var(--g-primary)]"
                />
                {t(`stock.${s}`)}
              </label>
            ))}
          </fieldset>
          <button
            type="button"
            onClick={handleBulkStock}
            disabled={bulkMutation.isPending}
            data-testid="bulk-apply-stock"
            className="inline-flex min-h-[44px] items-center rounded-[var(--g-radius)] bg-primary px-3 text-sm font-semibold text-[var(--g-on-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("bulk.apply")}
          </button>
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={deleteMutation.isPending}
            data-testid="bulk-delete"
            className="inline-flex min-h-[44px] items-center rounded-[var(--g-radius)] border border-[var(--g-error)]/40 px-3 text-sm font-semibold text-[var(--g-error)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("bulk.softDelete")}
          </button>
          <button
            type="button"
            onClick={clearSelection}
            data-testid="bulk-clear"
            className="inline-flex min-h-[44px] items-center px-2 text-sm text-muted-foreground underline"
          >
            {t("bulk.clear")}
          </button>
        </div>
      ) : null}

      {/* Error state */}
      {queryError ? (
        <div
          role="alert"
          className="rounded-[var(--g-radius)] border border-[var(--g-error)]/40 bg-[var(--g-error)]/5 p-6 text-center"
        >
          <p className="mb-3 text-sm text-muted-foreground">{t("list.error")}</p>
          <button
            type="button"
            onClick={() => void productsQuery.refetch()}
            className="inline-flex min-h-[44px] items-center rounded-[var(--g-radius)] bg-primary px-4 text-sm font-semibold text-[var(--g-on-primary)]"
          >
            {t("list.retry")}
          </button>
        </div>
      ) : productsQuery.isLoading ? (
        <div
          role="status"
          aria-live="polite"
          aria-label={t("loading")}
          className="flex justify-center py-12"
        >
          <span
            aria-hidden="true"
            className="size-8 animate-spin rounded-full border-2 border-[var(--g-hairline)] border-t-[var(--g-primary)]"
          />
        </div>
      ) : products.length === 0 ? (
        <div className="rounded-[var(--g-radius)] border border-border bg-card p-10 text-center" data-testid="product-list-empty">
          <p className="mb-1 text-sm font-semibold text-foreground">{t("list.empty")}</p>
          <p className="text-sm text-muted-foreground">{t("list.emptyHint")}</p>
        </div>
      ) : (
        <>
          {/* Desktop / tablet table (≥640px) */}
          <div className="hidden overflow-x-auto rounded-[var(--g-radius)] border border-border sm:block">
            <table className="w-full text-sm" data-testid="product-table">
              <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th scope="col" className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label={t("bulk.selectAll")}
                      data-testid="select-all"
                      className="size-4 accent-[var(--g-primary)]"
                    />
                  </th>
                  <th scope="col" className="px-3 py-2">{t("list.columns.photo")}</th>
                  <SortableTh column="name" label={t("list.columns.name")} sort={sort} onSort={toggleSort} ascLabel={t("list.sortAsc")} descLabel={t("list.sortDesc")} />
                  <SortableTh column="category" label={t("list.columns.category")} sort={sort} onSort={toggleSort} ascLabel={t("list.sortAsc")} descLabel={t("list.sortDesc")} />
                  <SortableTh column="price" label={t("list.columns.price")} sort={sort} onSort={toggleSort} ascLabel={t("list.sortAsc")} descLabel={t("list.sortDesc")} />
                  <SortableTh column="stock" label={t("list.columns.stock")} sort={sort} onSort={toggleSort} ascLabel={t("list.sortAsc")} descLabel={t("list.sortDesc")} />
                  <SortableTh column="status" label={t("list.columns.status")} sort={sort} onSort={toggleSort} ascLabel={t("list.sortAsc")} descLabel={t("list.sortDesc")} />
                  <SortableTh column="updated" label={t("list.columns.updated")} sort={sort} onSort={toggleSort} ascLabel={t("list.sortAsc")} descLabel={t("list.sortDesc")} />
                  <th scope="col" className="px-3 py-2">{t("list.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {pageProducts.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid="product-row">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(p.id)}
                        onChange={() => toggleRow(p.id)}
                        aria-label={t("bulk.selectRow", { name: p.name })}
                        data-testid={`select-${p.id}`}
                        className="size-4 accent-[var(--g-primary)]"
                      />
                    </td>
                    <td className="px-3 py-2">
                      {p.images[0] ? (
                        <img
                          src={p.images[0].thumb_url}
                          alt=""
                          className="size-10 rounded-[var(--g-radius)] object-cover"
                          data-testid={`row-thumb-${p.id}`}
                        />
                      ) : (
                        <div
                          aria-label={t("list.noPhoto")}
                          className="flex size-10 items-center justify-center rounded-[var(--g-radius)] bg-muted text-[10px] text-muted-foreground"
                        >
                          {t("list.noPhoto")}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium text-foreground">{p.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{categoryLabel(p.category_id)}</td>
                    <td className="px-3 py-2 tabular-nums text-foreground">
                      {formatPriceBand(p.price_kgs, p.price_max_kgs, t("list.noPrice"))}
                    </td>
                    <td className="px-3 py-2">
                      <GPill tone={stockTone(p.stock_state)} size="sm">
                        {t(`stock.${p.stock_state}`)}
                      </GPill>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col items-start gap-1">
                        <GPill tone={productStatusTone(p.status)} size="sm">
                          {t(`list.status.${p.status}`, { defaultValue: p.status })}
                        </GPill>
                        {p.status === "rejected" && p.review_reason ? (
                          <span
                            title={p.review_reason}
                            data-testid={`sent-back-reason-${p.id}`}
                            className="max-w-[200px] truncate text-xs font-medium text-[var(--g-error)]"
                          >
                            {t("list.sentBackReason", { reason: p.review_reason })}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                      {formatUpdated(p.updated_at, i18n.language)}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/sellers/me/products/${p.id}`}
                        className="inline-flex min-h-[44px] items-center text-sm font-semibold text-primary underline"
                        data-testid={`edit-${p.id}`}
                      >
                        {t("list.edit")}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards (<640px) */}
          <ul className="space-y-3 sm:hidden" data-testid="product-cards">
            {pageProducts.map((p) => (
              <li
                key={p.id}
                className="rounded-[var(--g-radius)] border border-border bg-card p-3"
                data-testid="product-card"
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleRow(p.id)}
                    aria-label={t("bulk.selectRow", { name: p.name })}
                    className="mt-1 size-5 accent-[var(--g-primary)]"
                  />
                  {p.images[0] ? (
                    <img
                      src={p.images[0].thumb_url}
                      alt=""
                      className="size-12 shrink-0 rounded-[var(--g-radius)] object-cover"
                    />
                  ) : (
                    <div
                      aria-label={t("list.noPhoto")}
                      className="flex size-12 shrink-0 items-center justify-center rounded-[var(--g-radius)] bg-muted text-[10px] text-muted-foreground"
                    >
                      {t("list.noPhoto")}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-foreground">{p.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {categoryLabel(p.category_id)}
                    </p>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="tabular-nums text-sm text-foreground">
                        {formatPriceBand(p.price_kgs, p.price_max_kgs, t("list.noPrice"))}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <GPill tone={stockTone(p.stock_state)} size="sm">
                          {t(`stock.${p.stock_state}`)}
                        </GPill>
                        <GPill tone={productStatusTone(p.status)} size="sm">
                          {t(`list.status.${p.status}`, { defaultValue: p.status })}
                        </GPill>
                      </div>
                    </div>
                    {p.status === "rejected" && p.review_reason ? (
                      <p
                        title={p.review_reason}
                        data-testid={`sent-back-reason-mobile-${p.id}`}
                        className="mt-1 truncate text-xs font-medium text-[var(--g-error)]"
                      >
                        {t("list.sentBackReason", { reason: p.review_reason })}
                      </p>
                    ) : null}
                    <Link
                      to={`/sellers/me/products/${p.id}`}
                      className="mt-2 inline-flex min-h-[44px] items-center text-sm font-semibold text-primary underline"
                    >
                      {t("list.edit")}
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* SCRUM-193 — numbered pages replace "Show more". The numbers chunk
              ALREADY-FETCHED cursor pages; stepping past the end fetches the
              next one, exactly as the old button did. */}
          {filteredProducts.length > 0 &&
          totalPages > 1 ? (
            <nav
              aria-label={t("list.paginationLabel")}
              data-testid="seller-products-pagination"
              className="mt-4 flex items-center justify-center gap-1"
            >
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                aria-label={t("list.previousPage")}
                data-testid="seller-products-page-prev"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-sm text-muted-foreground hover:bg-[var(--g-ground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
              >
                ‹
              </button>
              {pageWindow(currentPage, totalPages).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => goToPage(n)}
                  aria-label={t("list.page", { n })}
                  aria-current={n === currentPage ? "page" : undefined}
                  data-testid={`seller-products-page-${n}`}
                  className={[
                    "inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    n === currentPage
                      ? "bg-primary text-[var(--g-on-primary)]"
                      : "text-muted-foreground hover:bg-[var(--g-ground)]",
                  ].join(" ")}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                aria-label={t("list.nextPage")}
                data-testid="seller-products-page-next"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-sm text-muted-foreground hover:bg-[var(--g-ground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
              >
                ›
              </button>
            </nav>
          ) : null}
        </>
      )}
    </div>
  );
}

export default ProductListPage;
